import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3d543b20"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "find-contacts-container" }
const _hoisted_2 = { class: "grid-container" }

import { computed, ref, inject, watch, nextTick } from "vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { ContactFindResult } from "@/types/find/contacts/ContactFindResult";
import { getErrorMessage } from "bright-logic-core/src/errors";
import ModalBase from "@/components/ui/modals/modal-base.vue";
import FindInput from "@/components/find/find-input.vue";
import { DxToolbarItem } from "devextreme-vue/popup";
import FindContactResults from "@/components/find/contacts/find-contact-results.vue";
import { ContactFindQuery } from "@/types/find/contacts/ContactFindQuery";
import displayErrorMessage from "@/utils/ErrorHandling";
import { displayQuestionAlert } from "@/alerts/questionAlert";
import { Contacts } from "acquaint-api/models/Contacts";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import FilterContactFindResults from "@/components/find/contacts/filter-contact-find-results.vue";
import { FindContactFilters } from "@/types/contacts/FindContactFilters";
import GridFilterSummary from "@/components/ui/grid/grid-filter-summary.vue";
import { GridFilter } from "@/types/ui/GridFilter";
import { DefaultContactType } from "acquaint-api/constants/DefaultContactType";
import { ContactStatus } from "@/enums/contacts/ContactStatus";
import { useContactHelper } from "@/utils/contacts/useContactHelper";
import { SystemContactTypes } from "@/enums/contacts/SystemContactTypes";


export default /*#__PURE__*/_defineComponent({
  __name: 'find-contact-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },

  /**
   * Restricts the type of contacts that can be found.
   * @type {Number}
   * @default 0 (no restriction)
   */
  findTypeRestriction: {
    type: Number,
    default: 0,
  },

  /**
   * The title displayed for the find contact component.
   * @type {String}
   * @default 'Find Contact'
   */
  title: {
    type: String,
    default: "Find Contact",
  },

  /**
   * The ID of the currently selected contact.
   * @type {Number}
   * @default 0 (no contact selected)
   */
  currentContactId: {
    type: Number,
    default: 0,
  },

  /**
   * Controls the visibility of the "New Contact" button.
   * @type {Boolean}
   * @default false (button is hidden)
   */
  showNewContactButton: {
    type: Boolean,
    default: false,
  },

  /**
   * Specifies the type of contact to search for.
   * @type {Number}
   * @default -1 (no specific type)
   */
  typeToFind: {
    type: Number,
    default: -1,
  },
  /**
   * Specifies the default contact status to search for.
   * @type {Number}
   * @default -1 (no specific status)
   */
  defaultStatus: {
    type: Number,
    default: -1,
  },
  /**
   * The field name which will be used to disable contacts from grid.
   * @type {string}
   * @default ''
   */
  restrictedFieldName: {
    type: String,
    default: "",
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Triggered when a contact is selected from the list.
   * @param {Object} selectedContact - The selected contact object.
   * @param {Array} contactIds - List of all contact IDs.
   * @param {Number} index - The index of the selected contact.
   */
  "contactSelected",
  /**
   * Triggered when the user clicks to open the new contact creation modal.
   * Used to initiate the creation of a new contact.
   */
  "openNewContactModal",
  /**
   * Triggered when a primary contact is selected.
   * @param {Number} linkedTo - ID of the primary contact linked to the selected one.
   * @param {String} primaryContactName - The name or description of the primary contact.
   */
  "primaryContactSelected",
],
  setup(__props, { emit }) {

const props = __props;





const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const { getSystemContactTypeByTypeId } = useContactHelper();
const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const contacts = ref<ContactFindResult[]>([]);
const searching = ref(false);
/** Stores the current index of the row selected, used for the Contact Record Navigation Store on the Find Contact screen */
const index = ref<number>(0);
const selectedContact = ref<ContactFindResult>();
const primaryContactName = ref<IDDescriptionPair>();
const selectedContactFurtherDetails = ref<Contacts>();
const showFiltersModal = ref(false);
const filters = ref<FindContactFilters>({
  type: { id: props.typeToFind, description: "" },
  status: { id: -1, description: "" },
  branch: { id: -1, description: "" },
});

const searchQuery = ref("");

const onInputChanged = async (query: string) => {
  searchQuery.value = query;
  // To avoid unnecessary api calls, only perform search if user has entered more than 2 characters
  if (!searchQuery.value || searchQuery.value.length < 2) {
    selectedContact.value = undefined;
    contacts.value = [];
    return;
  }

  // Get results back from api
  await fetchContacts();
};

const fetchContacts = async () => {
  searching.value = true;
  const query: ContactFindQuery = {
    textToFind: searchQuery.value,
    maxResults: 200,
    findTypeRestriction: props.findTypeRestriction,
    currentContactId: props.currentContactId,
    filters: {
      statusID: filters.value.status.id,
      typeID: filters.value.type.id,
      userID: -1,
      branchID: filters.value.branch.id,
    },
  };

  try {
    contacts.value = await apiClient.httpPost<ContactFindResult[]>(
      "find/contact",
      query,
    );
  } catch (ex) {
    displayErrorMessage(getErrorMessage(ex));
    contacts.value = [];
  }
  searching.value = false;
};

/** Handles the Select button click event */
const onSelectClick = async () => {
  if (!selectedContact.value) {
    return;
  }

  await checkIfContactIsLinked(selectedContact.value);
};

/** Handles the row click event of the Find Contact Results */
const onRowClick = async (e: ContactFindResult) => {
  if (!e) {
    return;
  }

  await checkIfContactIsLinked(e);
};

/** Handles the row selected event of the Find Contact Results */
const onRowSelected = async (e: ContactFindResult, rowIndex: number) => {
  if (e) {
    selectedContact.value = e;
  }
  // store the index of the Row selected, so we can use this in the Contact Record Navigation Store on the Find Contact screen
  index.value = rowIndex;
};

// Handles the user clicking on the ... button on a row, the if statement ensure that if e is undefined, it doesnt disbale the select button
const adaptiveRowSelection = async (e: ContactFindResult) => {
  if (!e) {
    return;
  }

  selectedContact.value = e;
};

/** Emits the selected contact and closes the modal */
const emitSelectedContact = () => {
  showModal.value = false;
  emit(
    "contactSelected",
    selectedContact.value,
    contacts.value.map((item) => item.id),
    index.value,
  );
  contacts.value = [];
};

const closeModal = () => {
  emit("contactSelected", undefined);
  showModal.value = false;
  contacts.value = [];
};

const contactType = ref(-1);
watch(
  () => showModal.value,
  async (value) => {
    if (value) {
      selectedContact.value = undefined;
      contacts.value = [];
      contactType.value = props.typeToFind;

      let description = "";
      if (props.typeToFind === DefaultContactType.Tenant) {
        description = "Tenant";
      } else if (props.typeToFind === DefaultContactType.Guarantor) {
        description = "Guarantor";
        const typeId = await getSystemContactTypeByTypeId(
          SystemContactTypes.Guarantor,
        );
        contactType.value = typeId;
      } else if (props.typeToFind === DefaultContactType.PermittedOccupier) {
        description = "Permitted Occupier";
      }

      let status = "";
      if (props.defaultStatus === ContactStatus.Current) {
        status = "Current";
      }

      filters.value = {
        type: { id: contactType.value, description: description },
        status: { id: props.defaultStatus, description: status },
        branch: { id: -1, description: "" },
      };
    }
  },
);

// Code that will focus the search bar - it has a timeout to allow the animation to complete
const searchInputRef = ref();
watch(searchInputRef, async (value) => {
  if (value) {
    await nextTick();
    setTimeout(() => {
      value.focus();
    }, 400);
  }
});

const onNewContactClick = () => {
  showModal.value = false;
  emit("openNewContactModal");
};

// Code that displays a primary contact prompt if the contact selected is a linked contact

const fetchContactDetails = async (contactId: number) => {
  try {
    selectedContactFurtherDetails.value = await apiClient.httpGet<Contacts>(
      `database/contacts/${contactId}`,
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    selectedContact.value = undefined;
  }
};

const fetchPrimaryContactName = async (contactId: number) => {
  try {
    primaryContactName.value = await apiClient.httpGet(
      `/app/contacts/${contactId}/name`,
    );
    return true;
  } catch (err) {
    primaryContactName.value = undefined;
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const checkIfContactIsLinked = async (e: ContactFindResult) => {
  // We want to check to see if the the property is a link and if its is, we will inform the user of this.

  await fetchContactDetails(e.id);

  if (selectedContactFurtherDetails.value?.linkedTo) {
    // If we cannot fetch the primary contact's name, we wont display the prompt as there isn't any point.
    if (
      !(await fetchPrimaryContactName(
        selectedContactFurtherDetails.value?.linkedTo,
      ))
    ) {
      selectedContact.value = e;
      emitSelectedContact();
      return;
    }

    displayQuestionAlert(
      `This contact is a Link. Would you prefer to use the Primary record instead? \n \n ${
        primaryContactName.value?.description ?? ""
      }`,
    ).then((result) => {
      if (!result.isConfirmed) {
        selectedContact.value = e;
        emitSelectedContact();
        return;
      }

      selectedContact.value = {
        id: selectedContactFurtherDetails.value?.linkedTo ?? 0,
        name: "",
        address: "",
        telephoneNumber: "",
        mobileNumber: "",
        emailAddress: "",
        type: { id: 0, description: "" },
        status: { id: 0, description: "" },
        altReference: "",
        isRestricted: false,
        marketingPreferences: {
          email: false,
          telephone: false,
          smsText: false,
          confirmed: false,
        },
      };

      emit(
        "primaryContactSelected",
        selectedContactFurtherDetails.value?.linkedTo,
        primaryContactName.value?.description,
      );
      showModal.value = false;
    });
  } else {
    selectedContact.value = e;
    emitSelectedContact();
  }
};

const filtersApplied = async (selectedFilters: FindContactFilters) => {
  filters.value = JSON.parse(JSON.stringify(selectedFilters));

  // There is no point in calling the API after the filters have been updated if there are no search
  // results already to filter
  if (contacts.value.length > 0) {
    await fetchContacts();
  }
};

const summaryFilters = computed(() => {
  const gridFilters: GridFilter[] = [];

  // No filters so return blank array so summary shows All
  if (!filters.value) {
    return [];
  }

  if (filters.value.status.id > -1) {
    gridFilters.push({
      title: "Statuses",
      values: [filters.value.status],
      dataSource: [],
    });
  }
  if (filters.value.type.id > -1) {
    gridFilters.push({
      title: "Types",
      values: [filters.value.type],
      dataSource: [],
    });
  }
  if (filters.value.branch.id > -1) {
    gridFilters.push({
      title: "Branches",
      values: [filters.value.branch],
      dataSource: [],
    });
  }

  return gridFilters;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalBase, {
      title: props.title,
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
      height: "80vh",
      "show-close-button": true,
      onModalClosed: _cache[2] || (_cache[2] = ($event: any) => (emit('contactSelected', undefined)))
    }, {
      "non-scrollable-content": _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          (showModal.value)
            ? (_openBlock(), _createBlock(FindInput, {
                key: 0,
                ref_key: "searchInputRef",
                ref: searchInputRef,
                onChanged: onInputChanged
              }, null, 512))
            : _createCommentVNode("", true),
          _createVNode(GridFilterSummary, {
            rowCount: contacts.value.length,
            filters: summaryFilters.value,
            onFilterClick: _cache[0] || (_cache[0] = ($event: any) => (showFiltersModal.value = true)),
            dropdownMenuDisable: false,
            showMenu: false
          }, null, 8, ["rowCount", "filters"]),
          _createElementVNode("div", _hoisted_2, [
            _createVNode(FindContactResults, {
              data: contacts.value,
              searching: searching.value,
              onRowClick: onRowClick,
              onRowSelected: onRowSelected,
              onAdaptiveRowSelection: adaptiveRowSelection,
              "restricted-field-name": __props.restrictedFieldName
            }, null, 8, ["data", "searching", "restricted-field-name"])
          ])
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'New Contact',
          disabled: false,
          visible: props.showNewContactButton,
          onClick: () => onNewContactClick(),
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Select',
          disabled: !selectedContact.value,
          onClick: () => onSelectClick(),
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: { text: 'Close', onClick: () => closeModal() }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(FilterContactFindResults, {
      modelValue: showFiltersModal.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showFiltersModal).value = $event)),
      filters: filters.value,
      onFiltersApplied: filtersApplied
    }, null, 8, ["modelValue", "filters"])
  ], 64))
}
}

})